import { useState } from 'react';

interface Step {
  id: number;
  step: number;
  title: string;
  description: string;
  icon: string;
}

interface StepsTabProps {
  data: Step[];
  add: (item: Omit<Step, 'id'> & { id?: number | string }) => Promise<void>;
  update: (id: number, updates: Partial<Step>) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

export default function StepsTab({ data, add, update, remove }: StepsTabProps) {
  const [editing, setEditing] = useState<Step | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-brand-black text-xl">Cómo Funciona</h3>
        <button
          onClick={() => {
            setIsAdding(true);
            setEditing(null);
          }}
          className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
        >
          + Nuevo paso
        </button>
      </div>

      {(isAdding || editing) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const payload = {
              step: Number(fd.get('step')),
              title: fd.get('title') as string,
              description: fd.get('description') as string,
              icon: fd.get('icon') as string,
            };
            if (editing) {
              update(editing.id, payload);
              setEditing(null);
            } else {
              add(payload);
              setIsAdding(false);
            }
          }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h4 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Paso' : 'Nuevo Paso'}
            </h4>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
              }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Nº paso
              </label>
              <input
                name="step"
                type="number"
                defaultValue={editing?.step ?? data.length + 1}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Título
              </label>
              <input
                name="title"
                defaultValue={editing?.title ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Icono
              </label>
              <input
                name="icon"
                defaultValue={editing?.icon ?? 'ri-check-line'}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Descripción
            </label>
            <textarea
              name="description"
              defaultValue={editing?.description ?? ''}
              rows={3}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
              }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data
          .sort((a, b) => a.step - b.step)
          .map((s) => (
            <div
              key={s.id}
              className="bg-white border border-brand-gray-200 rounded-2xl p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-serif text-brand-black text-2xl font-light">
                  0{s.step}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      setEditing(s);
                      setIsAdding(false);
                    }}
                    className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                  >
                    <i className="ri-pencil-line text-xs" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`¿Eliminar paso ${s.step}?`))
                        remove(s.id);
                    }}
                    className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black mb-3">
                <i className={`${s.icon} text-lg`} />
              </div>
              <h4 className="font-sans text-brand-black text-sm font-medium mb-1">
                {s.title}
              </h4>
              <p className="font-sans text-brand-gray-500 text-xs leading-relaxed">
                {s.description}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
}